import { Imprint } from 'components/custom/Imprint';
import { useSettingsContext } from 'context/useSettingsContext';
import { PermissionRegistry } from 'helpers/auth/RBAC_Registry';
import i18n from 'i18n';
import AuthSimpleLayout from 'layouts/AuthSimpleLayout';
import LizenzPage from 'pages/UserSpecificPages/Admin/Lizenz';
import MailTemplatesManager from 'pages/UserSpecificPages/Admin/MailManager/MailTemplatesTable/MailTemplatesManager';
import CustomerAdminPage from 'pages/UserSpecificPages/CustomerAdmin/EditCustomer';
import { lazy, Suspense, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { ErrorBoundary } from 'react-error-boundary';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import ProtectedRoute from 'Routes';
import routes from '../routing/routes';
import CustomerAdminLayout from './admin/CustomerAdminLayout';
import RequirePermission from './RequirePermission';
const EditCustomerPage = lazy(
  () => import('pages/UserSpecificPages/Admin/EditCustomer/Index')
);

const ErroPage = lazy(() => import('pages/Error/ErroPage'));
const ShowCustomerPage = lazy(
  () => import('pages/UserSpecificPages/Admin/DetailCustomerPage/Index')
);
const EditUserPage = lazy(
  () => import('pages/UserSpecificPages/Admin/EditUser')
);
const Login = lazy(() => import('pages/Login'));
const Main = lazy(() => import('pages/Main'));
const MainLayout = lazy(() => import('layouts/MainLayout'));
const Error404 = lazy(() => import('components/errors/Error404'));
const ErrorLayout = lazy(() => import('layouts/ErrorLayout'));
const Downloads = lazy(() => import('pages/Downloads'));
const License = lazy(() => import('pages/License'));
const Profile = lazy(() => import('pages/Profile'));
const Company = lazy(() => import('pages/Company'));
const Support = lazy(() => import('pages/Support'));
const Reset = lazy(() => import('pages/Reset'));
const ForgetPassword = lazy(() => import('pages/ForgotPassword'));
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const TestElement = lazy(() => import('./admin/TestElement'));
const Benutzerliste = lazy(
  () => import('pages/UserSpecificPages/Admin/Benutzerliste')
);
const Kundenliste = lazy(
  () => import('pages/UserSpecificPages/Admin/Kundeliste')
);
const MailManagerPage = lazy(
  () => import('pages/UserSpecificPages/Admin/MailManager')
);
const SoftwarePage = lazy(
  () => import('pages/UserSpecificPages/Admin/Software/Index')
);
const ExternalConnectionsPage = lazy(
  () => import('pages/UserSpecificPages/Admin/ExternalConnections')
);
const AuditLogPage = lazy(
  () => import('pages/UserSpecificPages/Admin/AuditLog')
);
const ReportsPage = lazy(() => import('pages/UserSpecificPages/Admin/Reports'));
const ReportsDashboard = lazy(
  () => import('pages/UserSpecificPages/Admin/Reports/Dashboard')
);
const ReportsDownloads = lazy(
  () => import('pages/UserSpecificPages/Admin/Reports/DownloadReportTable')
);
const PageLoadFallback = () => (
  <div
    className="d-flex justify-content-center align-items-center"
    style={{ minHeight: '100vh' }}
  >
    <Spinner animation="border" role="status" variant="primary">
      <span className="visually-hidden">Loading...</span>
    </Spinner>
  </div>
);

const RootNode = () => {
  const { config } = useSettingsContext();
  const userLanguage =
    useSelector((state: any) => state?.user?.user?.language) ??
    localStorage.getItem('i18nextLng') ??
    'de';

  useEffect(() => {
    if (!userLanguage) return;

    const lng = userLanguage.toLowerCase();

    i18n.changeLanguage(lng);
    localStorage.setItem('i18nextLng', lng);
  }, [userLanguage]);

  return (
    <>
      <ErrorBoundary FallbackComponent={ErroPage}>
        <Suspense fallback={<PageLoadFallback />}>
          <Routes>
            <Route element={<ProtectedRoute requiresAuth={false} />}>
              <Route element={<AuthSimpleLayout />}>
                <Route path={routes.unsigned.signin} element={<Login />} />
                <Route path={routes.unsigned.reset} element={<Reset />} />
                <Route
                  path={routes.unsigned.forgotPassword}
                  element={<ForgetPassword />}
                />
              </Route>
            </Route>
            <Route element={<ProtectedRoute requiresAuth={true} />}>
              <Route element={<MainLayout />}>
                //#region MainAppRoutes
                <Route path={routes.signed.application} element={<Main />} />
                <Route path={routes.signed.download} element={<Downloads />} />
                <Route path={routes.signed.license} element={<License />} />
                <Route path={routes.signed.profile} element={<Profile />} />
                <Route path={routes.signed.company} element={<Company />} />
                <Route path={routes.signed.support} element={<Support />} />
                <Route path={routes.signed.imprint} element={<Imprint />} />
                //#region adminRoutes
                <Route path={routes.admin.index} element={<AdminLayout />}>
                  <Route
                    path={routes.admin.Licenses}
                    element={<LizenzPage />}
                  />
                  <Route
                    path={routes.admin.users}
                    element={<Benutzerliste />}
                  />
                  <Route
                    path={routes.admin.customers}
                    element={<Kundenliste />}
                  />
                  <Route
                    path={routes.admin.edituser}
                    element={<EditUserPage />}
                  />
                  <Route
                    path={routes.admin.showcustomer}
                    element={<ShowCustomerPage />}
                  />
                  <Route
                    path={routes.admin.editcustomer}
                    element={<EditCustomerPage />}
                  />
                  <Route
                    path={routes.admin.mailmanager}
                    element={<MailManagerPage />}
                  />
                  <Route
                    path={routes.admin.software}
                    element={<SoftwarePage />}
                  />
                  <Route
                    path={routes.admin.externalConnections}
                    element={
                      <RequirePermission
                        permission={
                          PermissionRegistry.InternalAdmin
                            .SettingsExternalManage
                        }
                      >
                        <ExternalConnectionsPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path={routes.admin.auditLog}
                    element={
                      <RequirePermission
                        permission={
                          PermissionRegistry.InternalAdmin.AdminReportRead
                        }
                      >
                        <AuditLogPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/admin/mail/templates"
                    element={<MailTemplatesManager />}
                  />
                  <Route path={routes.admin.reports} element={<ReportsPage />}>
                    <Route
                      index
                      element={<Navigate to="dashboard" replace />}
                    />
                    <Route path="dashboard" element={<ReportsDashboard />} />
                    <Route path="downloads" element={<ReportsDownloads />} />
                  </Route>
                </Route>
                //#endregion adminRoutes //#region CustomerAdminRoutes
                <Route
                  path={routes.customeradmin.index}
                  element={<CustomerAdminLayout />}
                >
                  <Route
                    path={routes.customeradmin.editcustomer}
                    element={<CustomerAdminPage />}
                  />
                  <Route
                    path={routes.customeradmin.edituser}
                    element={<EditUserPage />}
                  />
                </Route>
                //#endregion CustomerAdminRoutes
              </Route>
              //#region Catch unmatched authenticated routes
              <Route
                path={routes.signed.application + '/*'}
                element={<Navigate to={routes.signed.notfound} />}
              />
            </Route>
            //#region Erro Routes
            <Route element={<ErrorLayout />}>
              <Route path={routes.signed.notfound} element={<Error404 />} />
              <Route path={routes.unsigned.imprint} element={<Imprint />} />
            </Route>
            //#region Default Redirects
            <Route
              path="/"
              element={<Navigate to={routes.unsigned.signin} replace />}
            />
            <Route
              path="*"
              element={<Navigate to={routes.signed.notfound} />}
            />
          </Routes>
        </Suspense>
      </ErrorBoundary>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={config.isDark ? 'dark' : 'light'}
      />
    </>
  );
};

export default RootNode;
